import { AgGridFilterType, AgGridServerFilterMap, BasicTableOptions, ExternalTableFilter } from '@tassat/common-definitions';
import { FirmRecord } from '@tassat/fred-definitions';

export interface TransactionHistoryTableOptions
    extends Omit<BasicTableOptions, 'SelectedFilter'> {
    SelectedFilter: 'approval' | 'clientApproval' | 'rejected' | 'failed' | 'pending' | 'none';
    SelectedFirm: string;
    SelectedFirmName: string;
    FirmCondition: string;
    SelectedWallet?: string;
    WalletCondition?: string;
    SelectedCurrency?: number;
    CurrencyCondition?: string;
}

type HistoryTableFilter = { column: string; operation: AgGridServerFilterMap; value: string; dbColumn?: string };

// Keeping for backwards compatibility
const clearSpecialTransactionColumns = (
    mappedColumn: string,
    currentFilters: ExternalTableFilter[]
): ExternalTableFilter[] => {
    // Clear adjustment flag if necessary when clearing transaction type filter
    if (mappedColumn === 'tranType') {
        const adjustmentFilter = currentFilters.findIndex((filter) => filter.column === 'adjustment');
        if (adjustmentFilter !== -1) {
            currentFilters.splice(adjustmentFilter, 1);
        }
    } else if (mappedColumn === 'recipientWalletName1' || mappedColumn === 'senderWalletName1') {
        // handle client counterparty field which can be split in two directions
        const senderFilter = currentFilters.findIndex((filter) => filter.column === 'senderWalletName1');
        if (senderFilter !== -1) {
            currentFilters.splice(senderFilter, 1);
        }

        const recipientFilter = currentFilters.findIndex((filter) => filter.column === 'recipientWalletName1');
        if (recipientFilter !== -1) {
            currentFilters.splice(recipientFilter, 1);
        }
    }

    return currentFilters;
};

const adminClearSpecialTransactionColumns = (
    mappedColumn: string,
    currentFilters: ExternalTableFilter[]
): ExternalTableFilter[] => {
    // Clear adjustment flag if necessary when clearing transaction type filter
    if (mappedColumn === 'tranType') {
        const adjustmentFilter = currentFilters.findIndex((filter) => filter.column === 'adjustment');
        if (adjustmentFilter !== -1) {
            currentFilters.splice(adjustmentFilter, 1);
        }
    }

    return currentFilters;
};

const clientClearSpecialTransactionColumns = (
    mappedColumn: string,
    currentFilters: ExternalTableFilter[]
): ExternalTableFilter[] => {
    // Clear adjustment flag if necessary when clearing transaction type filter
    if (mappedColumn === 'tranType') {
        const adjustmentFilter = currentFilters.findIndex((filter) => filter.column === 'adjustment');
        if (adjustmentFilter !== -1) {
            currentFilters.splice(adjustmentFilter, 1);
        }
    }

    // Below is only needed for client queries where search is done by direction, so other side needs to be cleared
    if (mappedColumn === 'recipientWalletName1' || mappedColumn === 'senderWalletName1') {
        // handle client counterparty field which can be split in two directions
        const senderFilter = currentFilters.findIndex((filter) => filter.column === 'senderWalletName1');
        if (senderFilter !== -1) {
            currentFilters.splice(senderFilter, 1);
        }

        const recipientFilter = currentFilters.findIndex((filter) => filter.column === 'recipientWalletName1');
        if (recipientFilter !== -1) {
            currentFilters.splice(recipientFilter, 1);
        }
    } else if (mappedColumn === 'recipientFirm' || mappedColumn === 'senderFirm') {
        const senderFilter = currentFilters.findIndex((filter) => filter.column === 'senderFirm');
        if (senderFilter !== -1) {
            currentFilters.splice(senderFilter, 1);
        }

        const recipientFilter = currentFilters.findIndex((filter) => filter.column === 'recipientFirm');
        if (recipientFilter !== -1) {
            currentFilters.splice(recipientFilter, 1);
        }
    } else if (mappedColumn === 'recipientBank' || mappedColumn === 'senderBank') {
        const senderFilter = currentFilters.findIndex((filter) => filter.column === 'senderBank');
        if (senderFilter !== -1) {
            currentFilters.splice(senderFilter, 1);
        }

        const recipientFilter = currentFilters.findIndex((filter) => filter.column === 'recipientBank');
        if (recipientFilter !== -1) {
            currentFilters.splice(recipientFilter, 1);
        }
    }

    return currentFilters;
};

const createSpecialTransactionColumnFilters = (filter: ExternalTableFilter): ExternalTableFilter[] => {
    const {
        column: mappedColumn,
        operation,
        value,
        dbColumn
    } = filter;

    const op = AgGridServerFilterMap[operation];

    const filters: HistoryTableFilter[] = [];

    if (operation === AgGridFilterType.Equal && mappedColumn === 'tranType' && (value === 'A' || value === 'R')) {
        filters.push({
            column: 'adjustment',
            operation: op,
            value: value === 'A' ? 'true' : 'false'
        });
        filters.push({
            column: mappedColumn,
            operation: op,
            value: 'R'
        });
    } else {
        filters.push({
            column: mappedColumn,
            operation: op,
            value,
            dbColumn
        });
    }

    return filters;
};

// Keeping for backwards compatibility
export const transactionHistoryTableOptionsInitialState: TransactionHistoryTableOptions = {
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'auditTime|DESC',
    DefaultSort: 'auditTime|DESC',
    SelectedDateFilter: '1d',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: 'none',
    SelectedFirmName: '',
    FirmCondition: '',
    SelectedWallet: 'none',
    WalletCondition: '',
    TableFilters: [],
    FilterCondition: '',
    DefaultState: true,
    SelectedCurrency: 0,
    CurrencyCondition: '',
    clearSpecialColumns: clearSpecialTransactionColumns,
    createSpecialColumnFilters: createSpecialTransactionColumnFilters
};

export const adminTransactionHistoryTableOptionsInitialState: TransactionHistoryTableOptions = {
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'auditTime|DESC',
    DefaultSort: 'auditTime|DESC',
    SelectedDateFilter: '1d',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: 'none',
    SelectedFirmName: '',
    FirmCondition: '',
    SelectedWallet: 'none',
    WalletCondition: '',
    TableFilters: [],
    FilterCondition: '',
    SelectedCurrency: 0,
    CurrencyCondition: '',
    DefaultState: true,
    clearSpecialColumns: adminClearSpecialTransactionColumns,
    createSpecialColumnFilters: createSpecialTransactionColumnFilters
};

export const clientTransactionHistoryTableOptionsInitialState: TransactionHistoryTableOptions = {
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'auditTime|DESC',
    DefaultSort: 'auditTime|DESC',
    SelectedDateFilter: '1d',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: 'none',
    SelectedFirmName: '',
    FirmCondition: '',
    SelectedWallet: 'none',
    WalletCondition: '',
    TableFilters: [],
    FilterCondition: '',
    DefaultState: true,
    clearSpecialColumns: clientClearSpecialTransactionColumns,
    createSpecialColumnFilters: createSpecialTransactionColumnFilters
};

// Keeping for backwards compatibility
export const setTableOptionsInitialState = (firm: FirmRecord, walletId?: string): TransactionHistoryTableOptions => ({
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'auditTime|DESC',
    DefaultSort: 'auditTime|DESC',
    SelectedDateFilter: '1d',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: firm.FirmId,
    SelectedFirmName: firm.Name,
    FirmCondition: `firmId|EQ|${firm.FirmId}`,
    SelectedWallet: walletId || '',
    WalletCondition: walletId ? `wallet|EQ|${walletId}` : '',
    TableFilters: [],
    FilterCondition: '',
    DefaultState: true,
    clearSpecialColumns: clearSpecialTransactionColumns,
    createSpecialColumnFilters: createSpecialTransactionColumnFilters
});

export const setAdminTableOptionsInitialState = (
    firm: FirmRecord,
    walletId?: string
): TransactionHistoryTableOptions => ({
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'auditTime|DESC',
    DefaultSort: 'auditTime|DESC',
    SelectedDateFilter: '1d',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: firm.FirmId,
    SelectedFirmName: firm.Name,
    FirmCondition: `firmId|EQ|${firm.FirmId}`,
    SelectedWallet: walletId || '',
    WalletCondition: walletId ? `wallet|EQ|${walletId}` : '',
    TableFilters: [],
    FilterCondition: '',
    SelectedCurrency: 0,
    CurrencyCondition: '',
    DefaultState: true,
    clearSpecialColumns: adminClearSpecialTransactionColumns,
    createSpecialColumnFilters: createSpecialTransactionColumnFilters
});

export const setClientTableOptionsInitialState = (
    firm: FirmRecord,
    walletId?: string
): TransactionHistoryTableOptions => ({
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'auditTime|DESC',
    DefaultSort: 'auditTime|DESC',
    SelectedDateFilter: '1d',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: firm.FirmId,
    SelectedFirmName: firm.Name,
    FirmCondition: `firmId|EQ|${firm.FirmId}`,
    SelectedWallet: walletId || '',
    WalletCondition: walletId ? `wallet|EQ|${walletId}` : '',
    TableFilters: [],
    FilterCondition: '',
    DefaultState: true,
    clearSpecialColumns: clientClearSpecialTransactionColumns,
    createSpecialColumnFilters: createSpecialTransactionColumnFilters
});